/*
 * A crew member's own time off (owner, Nov 2: Viviana books her own life and is busy some days). They add a whole day or
 * part of one, today or later, and take their own off again. The office bell rings each time, naming any visit of theirs
 * then. English and Spanish, by their language.
 */
import { useState } from "react";
import { addDays, type TimeOffRow } from "@steward/shared";
import { useAddMyTimeOff, useClearMyTimeOff, useMyTimeOff } from "../api/hooks";
import { useSession } from "../app/session";
import { Choice, InputText } from "../components/fields";
import { clock, todayIn } from "../components/format";
import { useToast } from "../components/overlays";
import { Button, Card, EmptyState, Page, Skeleton } from "../components/parts";
import { errText } from "../components/work";

const WORDS = {
  en: {
    title: "Time off",
    intro: "Mark the days or hours you can't work. The office sees it right away.",
    add: "Add time off",
    firstDay: "First day",
    lastDay: "Last day",
    howLong: "How long",
    wholeDay: "Whole day",
    partDay: "Part of the day",
    from: "From",
    to: "To",
    why: "Note for the office (optional)",
    cancel: "Cancel",
    save: "Save",
    remove: "Take off",
    none: "No time off coming up",
    noneHint: "Add a day when you are busy, so the office does not book you.",
    saved: "Saved. The office can see it.",
    removed: "Taken off. The office can see it.",
    until: "to",
  },
  es: {
    title: "Días libres",
    intro: "Marca los días u horas que no puedes trabajar. La oficina lo ve enseguida.",
    add: "Agregar día libre",
    firstDay: "Primer día",
    lastDay: "Último día",
    howLong: "Cuánto tiempo",
    wholeDay: "Todo el día",
    partDay: "Parte del día",
    from: "Desde",
    to: "Hasta",
    why: "Nota para la oficina (opcional)",
    cancel: "Cancelar",
    save: "Guardar",
    remove: "Quitar",
    none: "No tienes días libres próximos",
    noneHint: "Agrega un día cuando no puedas trabajar, así la oficina no te programa.",
    saved: "Guardado. La oficina lo puede ver.",
    removed: "Quitado. La oficina lo puede ver.",
    until: "a",
  },
};

export function MyTimeOff() {
  const { company, me } = useSession();
  const es = me.locale === "es";
  const w = WORDS[es ? "es" : "en"];
  const today = todayIn(company.timezone);
  const off = useMyTimeOff();
  const add = useAddMyTimeOff();
  const clear = useClearMyTimeOff();
  const toast = useToast();
  const [draft, setDraft] = useState<{
    fromDay: string;
    toDay: string;
    whole: "day" | "part";
    fromTime: string;
    toTime: string;
    reason: string;
  } | null>(null);
  const dayWords = (d: string) =>
    new Intl.DateTimeFormat(es ? "es-US" : "en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }).format(
      new Date(`${d}T12:00:00Z`),
    );
  const when = (t: TimeOffRow) =>
    [
      t.fromDay === t.toDay ? dayWords(t.fromDay) : `${dayWords(t.fromDay)} ${w.until} ${dayWords(t.toDay)}`,
      t.fromTime && t.toTime ? `${clock(t.fromTime)} ${w.until} ${clock(t.toTime)}` : w.wholeDay,
    ].join(", ");

  return (
    <Page title={w.title} subtitle={w.intro}>
      <Card
        title={w.title}
        flush
        action={
          draft ? null : (
            <Button
              type="secondary"
              size="small"
              icon="plus"
              onClick={() =>
                setDraft({
                  fromDay: addDays(today, 1),
                  toDay: addDays(today, 1),
                  whole: "day",
                  fromTime: "09:00",
                  toTime: "13:00",
                  reason: "",
                })
              }
            >
              {w.add}
            </Button>
          )
        }
      >
        {draft ? (
          <div className="stack auto-step">
            <div className="row">
              <InputText
                label={w.firstDay}
                type="date"
                min={today}
                value={draft.fromDay}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    fromDay: e.target.value,
                    toDay: draft.toDay < e.target.value ? e.target.value : draft.toDay,
                  })
                }
              />
              <InputText
                label={w.lastDay}
                type="date"
                min={draft.fromDay}
                value={draft.toDay}
                onChange={(e) => setDraft({ ...draft, toDay: e.target.value })}
              />
            </div>
            <Choice
              label={w.howLong}
              value={draft.whole}
              options={[
                { value: "day", label: w.wholeDay },
                { value: "part", label: w.partDay },
              ]}
              onChange={(whole) => setDraft({ ...draft, whole })}
            />
            {draft.whole === "part" ? (
              <div className="row">
                <InputText
                  label={w.from}
                  type="time"
                  value={draft.fromTime}
                  onChange={(e) => setDraft({ ...draft, fromTime: e.target.value })}
                />
                <InputText
                  label={w.to}
                  type="time"
                  value={draft.toTime}
                  onChange={(e) => setDraft({ ...draft, toTime: e.target.value })}
                />
              </div>
            ) : null}
            <InputText
              label={w.why}
              value={draft.reason}
              maxLength={300}
              onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
            />
            <div className="row end">
              <Button type="tertiary" onClick={() => setDraft(null)}>
                {w.cancel}
              </Button>
              <Button
                loading={add.isPending}
                onClick={() =>
                  add.mutate(
                    {
                      fromDay: draft.fromDay,
                      toDay: draft.toDay,
                      ...(draft.whole === "part" ? { fromTime: draft.fromTime, toTime: draft.toTime } : {}),
                      ...(draft.reason.trim() ? { reason: draft.reason.trim() } : {}),
                    },
                    {
                      onSuccess: () => {
                        setDraft(null);
                        toast(w.saved);
                      },
                      onError: (e) => toast(errText(e), { kind: "error" }),
                    },
                  )
                }
              >
                {w.save}
              </Button>
            </div>
          </div>
        ) : null}
        {!off.data ? (
          <Skeleton rows={2} />
        ) : off.data.timeOff.length === 0 ? (
          draft ? null : (
            <EmptyState icon="schedule" title={w.none}>
              {w.noneHint}
            </EmptyState>
          )
        ) : (
          <ul className="auto-list">
            {off.data.timeOff.map((t) => (
              <li key={t.id} className="setting-row">
                <span className="what">
                  <b>{when(t)}</b>
                  {t.reason ? <span>{t.reason}</span> : null}
                </span>
                <Button
                  type="tertiary"
                  size="small"
                  loading={clear.isPending && clear.variables === t.id}
                  onClick={() =>
                    clear.mutate(t.id, {
                      onSuccess: () => toast(w.removed),
                      onError: (e) => toast(errText(e), { kind: "error" }),
                    })
                  }
                >
                  {w.remove}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Page>
  );
}
